import { useEffect, useState, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import format from 'date-fns/format';

//import { scrollToBottom } from "../../../../../../utils/messages";
import {
  MessageTypes,
  QuickButton,
  Link,
  CustomCompMessage,
  GlobalState,
  MessageRatingType,
} from '../../../../../../store/types';
import { setBadgeCount, markAllMessagesRead } from '../../../../../../store/actions';

import Loader from './components/Loader';
import QuickButtons from '../../components/QuickButtons';
import WelcomeBanner from '../WelcomeBanner';
import './styles.scss';
import '../QuickButtons/style.scss';
import { AnyFunction } from '../../../../../../../src/utils/types';
import { QuickReplyConfig } from 'src/utils/widgetSettings';

type TypingIndicatorObject = {
  key: string;
  stage?: string;
  partialContent?: string;
};

type Props = {
  showTimeStamp: boolean;
  profileAvatar?: string;
  onInteractiveButtonClicked?: AnyFunction;
  onHandleConversationScroll?: AnyFunction;
  fullScreenMode: boolean;
  handleRating: (messageId: string, type: MessageRatingType) => void;
  /**
   * IMBEE-8069 — pixels taken by chrome OUTSIDE this scroll container, which is the bottom
   * quick-button bar and nothing else. The widget sizes itself from this container's fixed
   * height, so anything rendered beside it grows the whole widget unless subtracted here.
   * Measured by Conversation rather than assumed: the bar disappears when every button is
   * filtered out.
   */
  reservedHeight?: number;
  /**
   * The channel's quick-reply bundle (src/utils/widgetSettings.ts), passed whole rather than
   * unpacked into separate props; Messages renders the welcome banner from it. Absent for a
   * channel without the feature.
   */
  quickReplyConfig?: QuickReplyConfig;
  /** Channel theme colour for the banner's image rail, hex WITHOUT a leading '#'. */
  themeColor?: string;
  /** Conversation's sendMessage — a banner button sends its own label as the visitor's message. */
  onBannerSelect: (label: string) => void;
};

function Messages({
  profileAvatar,
  showTimeStamp,
  onInteractiveButtonClicked,
  onHandleConversationScroll,
  fullScreenMode,
  handleRating,
  reservedHeight = 0,
  quickReplyConfig,
  themeColor,
  onBannerSelect,
}: Props) {
  const dispatch = useDispatch();
  const { messages, typing, showChat, badgeCount, typingIndicatorExpiries, ratingEnabled } = useSelector(
    (state: GlobalState) => ({
      messages: state.messages.messages,
      badgeCount: state.messages.badgeCount,
      typing: state.behavior.messageLoader,
      showChat: state.behavior.showChat,
      typingIndicatorExpiries: state.messages.typingIndicatorExpiries,
      ratingEnabled: state.messages.ratingEnabled,
    }),
  );

  const isShowingForm = useSelector((state: GlobalState) => state.behavior.showContactForm);
  const isLoadingPreviousMessages = useSelector((state: GlobalState) => state.behavior.isLoadingPreviousMessages);

  const messageRef = useRef<HTMLDivElement | null>(null);
  const messageContainerRef = useRef<HTMLDivElement | null>(null);
  const topSentinelRef = useRef<HTMLDivElement | null>(null);
  const [currentTimestamp, setCurrentTimestamp] = useState<number>(Date.now());

  /**
   * IMBEE-9725 — decided once, at mount, so the render and the effect below can never disagree
   * about which paging path is live. The `typeof` guard mirrors the repo's own precedent for an
   * observer that jsdom and older browsers may not provide — the
   * `typeof ResizeObserver === 'undefined'` guard at Conversation/index.tsx:139.
   */
  const [supportsIntersectionObserver] = useState(() => typeof IntersectionObserver !== 'undefined');

  /**
   * IMBEE-9725 — the re-entrancy latch for history paging.
   *
   * Nothing in the paging funnel deduplicates: neither handleConversationScroll,
   * getPreviousMessages() nor apiClient.fetchMessages(). A sentinel can intersect repeatedly
   * during one scroll gesture, so the guard belongs here.
   *
   * It cannot be the Redux isLoadingPreviousMessages flag: that is toggled at App.tsx:823/:1044,
   * both INSIDE fetchMessages().then(), so it is false for the whole network request, never
   * flips at all on an empty page, and is a toggle rather than a set. It is consulted below as a
   * cheap extra check, never as the guard.
   *
   * The latch is bounded by the request, not by a timer: apiClient sets no axios timeout, so a
   * stalled socket holds paging closed until the browser errors the connection and
   * getPreviousMessages' own catch resolves it. It self-heals rather than sticking, and putting
   * an arbitrary constant here would be the same mistake as an arbitrary scroll threshold.
   *
   * It guards the sentinel only. The onScroll fallback below is unlatched; it is reachable only
   * on a browser with no IntersectionObserver, and only when scrollTop actually changes to 0.
   */
  const pagingInFlight = useRef(false);

  /**
   * IMBEE-9725 — paging is armed by the sentinel having been OUT of view at least once.
   *
   * observe() delivers an INITIAL observation carrying the element's current state; it does not
   * wait for a change. On a mount where the store already holds messages — reopening the chat
   * (layout.tsx renders Conversation only while showChat, so it remounts), or a first open after
   * App's STOMP-connect getPreviousMessages() has landed — the container is still at scrollTop 0
   * and the sentinel sits just under the banner, so that first observation reports intersecting
   * and paging would fire with no visitor scroll at all.
   *
   * Measured in Chrome against this component's geometry: with a banner shorter than the
   * scroller, observe() alone produces one callback with isIntersecting true, ratio 1, at
   * scrollTop 0. It is not a race — scrollToBottom() below is on a 400ms timer, so the container
   * is still at the top when it arrives. A banner TALLER than the scroller reports false and
   * hides the whole thing, which is why it survives review more easily than production.
   *
   * Arming on a non-intersecting observation keeps the trigger offset-independent, which an
   * "ignore anything within N ms of mount" timer would not. The component's own scrollToBottom()
   * supplies that observation on every real mount, so paging is armed before the visitor could
   * plausibly scroll back up.
   */
  const sentinelArmed = useRef(false);

  /**
   * Read by the observer callback, which is created once and must not close over a stale render.
   * onHandleConversationScroll in particular is redefined on every App render, so depending on
   * its identity would tear the observer down and rebuild it constantly — and every rebuild
   * re-reports the sentinel's current intersection, which would fire paging again.
   */
  const onHandleConversationScrollRef = useRef(onHandleConversationScroll);
  const isLoadingPreviousMessagesRef = useRef(isLoadingPreviousMessages);
  const messageCountRef = useRef(messages.length);
  useEffect(() => {
    onHandleConversationScrollRef.current = onHandleConversationScroll;
    isLoadingPreviousMessagesRef.current = isLoadingPreviousMessages;
    messageCountRef.current = messages.length;
  }, [onHandleConversationScroll, isLoadingPreviousMessages, messages.length]);

  const activeTypingIndicators = useMemo(() => {
    const result: TypingIndicatorObject[] = [];
    const expires = typingIndicatorExpiries.filter((exp) => exp.expiry > currentTimestamp);
    let hasRaw = false;
    for (let i = 0; i < expires.length; i++) {
      const { responseIdentifier, stage, partialContent } = expires[i];
      if (!stage && !partialContent) {
        if (!hasRaw) {
          result.push({ key: 'raw' });
          hasRaw = true;
        }
      } else {
        result.push({
          key: responseIdentifier,
          stage,
          partialContent,
        });
      }
    }
    if (typing && !hasRaw) {
      result.push({ key: 'raw' });
    }
    return result;
  }, [typing, currentTimestamp, typingIndicatorExpiries]);

  const typingIndicatorContent = useMemo(() => {
    return JSON.stringify(activeTypingIndicators);
  }, [activeTypingIndicators]);

  const timestampTimer = useRef<number | null>(null);
  useEffect(() => {
    const refreshTimestamp = () => {
      setCurrentTimestamp(Date.now());
      timestampTimer.current = window.setTimeout(refreshTimestamp, 1000);
    };
    refreshTimestamp();
    return () => {
      timestampTimer.current && clearTimeout(timestampTimer.current);
    };
  }, []);

  useEffect(() => {
    if (typingIndicatorContent && messageContainerRef.current) {
      const { scrollTop, clientHeight, scrollHeight } = messageContainerRef.current;
      console.log(scrollTop, clientHeight, scrollHeight, scrollHeight - clientHeight, scrollTop + 100);
      if (scrollHeight - clientHeight <= scrollTop + 100) {
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
    }
  }, [typingIndicatorContent, messageContainerRef]);

  const scrollToBottom = () => {
    messageRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  /**
   * IMBEE-9725 — the fallback, attached ONLY when IntersectionObserver is missing (see the
   * container's onScroll below). Leaving it live alongside the sentinel would fire the callback
   * twice for one scroll to literal scrollTop === 0.
   */
  const onScroll = (e) => {
    if (e.target.scrollTop === 0) {
      onHandleConversationScroll?.(e);
    }
  };

  /**
   * IMBEE-9725 — history paging is triggered by a zero-height sentinel rendered between the
   * banner and the first message, observed with the scroll container as root.
   *
   * The banner is inside that container, so scrollTop === 0 is the top of the BANNER, not the
   * top of the oldest message: a trigger keyed to that offset would let a visitor scroll the
   * oldest message into view without ever reaching it, and history would silently appear to
   * end. A sentinel below the banner makes the trigger independent of banner height.
   *
   * The observer is built once and torn down only on unmount: every live value it reads comes
   * from a ref, because rebuilding it re-reports the sentinel's current intersection and would
   * fire paging again for free.
   *
   * One page per intersection, deliberately. The sentinel sits ABOVE the prepended messages and
   * nothing restores scroll position after a prepend, so re-arming the observer on completion
   * would find it still visible and page again immediately, draining the whole history in a
   * burst. The visitor's next scroll produces the next intersection.
   */
  useEffect(() => {
    if (!supportsIntersectionObserver) return undefined;
    const sentinel = topSentinelRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        // Out of view: nothing to do, but the visitor can now page by scrolling back to it.
        if (!entry?.isIntersecting) {
          sentinelArmed.current = true;
          return;
        }
        // Intersecting, but never seen out of view — this is observe()'s initial observation on
        // a mount that already has history. The visitor has not scrolled; see sentinelArmed.
        if (!sentinelArmed.current) return;
        // Nothing loaded yet, so there is nothing to load messages OLDER than. An empty
        // container makes the sentinel visible the instant it is observed, and App runs its own
        // getPreviousMessages() on STOMP connect (App.tsx:301) off the same timeStampOffset —
        // so firing here would race that load and prepend the same page twice, silently.
        // The visitor's own scroll is what should page, and that needs history to scroll.
        if (messageCountRef.current === 0) return;
        // pagingInFlight is the guard; the Redux flag is the cheap extra check (see its ref).
        if (pagingInFlight.current || isLoadingPreviousMessagesRef.current) return;

        pagingInFlight.current = true;
        // The funnel is invoked INSIDE the executor, not as `Promise.resolve(fn())`: a
        // synchronous throw there would otherwise escape this callback with the latch still
        // raised, and history paging would be dead for the rest of the session with no symptom
        // beyond a conversation that stops loading older messages. This form turns a throw into
        // a rejection, so the .finally below always runs.
        //
        // It also wraps the plain `undefined` the funnel returns when its own session /
        // contact-form / upload guards reject the call, so the latch clears immediately then too.
        new Promise((resolve) => resolve(onHandleConversationScrollRef.current?.()))
          .catch((error) => {
            // getPreviousMessages swallows its own network errors, so anything arriving here is
            // unreported. Say so rather than losing it: a silent paging failure looks exactly
            // like a conversation with no more history.
            console.error('history paging failed', error);
          })
          .finally(() => {
            pagingInFlight.current = false;
          });
      },
      { root: messageContainerRef.current },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [supportsIntersectionObserver]);

  useEffect(() => {
    // @ts-ignore
    if (!isShowingForm && !isLoadingPreviousMessages)
      setTimeout(() => {
        scrollToBottom();
      }, 400);

    if (showChat && badgeCount) dispatch(markAllMessagesRead());
    else dispatch(setBadgeCount(messages.filter((message) => message.unread).length));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, badgeCount, showChat]);

  const getComponentToRender = (message: MessageTypes | Link | CustomCompMessage) => {
    // console.log("message.quickButtons: ", message.quickButtons);
    const ComponentToRender = message.component;
    if (message.type === 'component') {
      return <ComponentToRender {...message.props} />;
    }
    return (
      <ComponentToRender
        message={message}
        showTimeStamp={showTimeStamp}
        onInteractiveButtonClicked={onInteractiveButtonClicked}
        handleRating={handleRating}
      />
    );
  };

  const getQuickButtonsToRender = (button: QuickButton, messageID: string | undefined) => {
    // console.log("button: ", button);
    const ComponentToRender = button.component;
    return (
      <ComponentToRender
        onInteractiveButtonClicked={onInteractiveButtonClicked}
        button={button}
        messageID={messageID}
      />
    );
  };

  // TODO: Fix this function or change to move the avatar to last message from response
  // const shouldRenderAvatar = (message: Message, index: number) => {
  //   const previousMessage = messages[index - 1];
  //   if (message.showAvatar && previousMessage.showAvatar) {
  //     dispatch(hideAvatar(index));
  //   }
  // }

  return (
    <div
      ref={messageContainerRef}
      id="messages"
      className="rcw-messages-container"
      onScroll={supportsIntersectionObserver ? undefined : onScroll}
      style={
        !fullScreenMode
          ? {
              // Unchanged when nothing extra is rendered, so a channel without the feature
              // keeps byte-identical sizing.
              height: isShowingForm
                ? `calc(450px${reservedHeight ? ` - ${reservedHeight}px` : ''})`
                : `calc(450px - 55px${reservedHeight ? ` - ${reservedHeight}px` : ''})`,
            }
          : { flex: 'auto' }
      }
    >
      {/*
        IMBEE-9725 — the welcome banner is the first child of the scroller, so it sits at the top
        of the conversation and scrolls away with it. It is a static child, NOT an entry in the
        messages array: entries there are keyed by timestamp, switched on message.type, and
        counted by the unread/badge logic above, so a banner in that array would need a fake
        timestamp and would corrupt the badge count. The container already holds non-message
        children (QuickButtons, the typing Loaders, the trailing scroll anchor).

        isShowingForm comes from Redux (state.behavior.showContactForm), read at the top of this
        component.
      */}
      <WelcomeBanner
        enabled={quickReplyConfig?.enableWelcomeBanner && !isShowingForm}
        imageMode={quickReplyConfig?.welcomeBannerImageMode}
        imageUrl={quickReplyConfig?.welcomeBannerImageUrl}
        buttons={quickReplyConfig?.welcomeBannerButtons}
        themeColor={themeColor}
        onSelect={onBannerSelect}
      />
      {/* Zero-height paging trigger — see the IntersectionObserver effect above. */}
      <div ref={topSentinelRef} data-testid="history-sentinel" aria-hidden="true" />

      {messages?.map((message, index) => (
        <div
          className={
            message.type === 'component'
              ? 'rcw-component'
              : message.quickButtons !== undefined
              ? `rcw-message-buttons ${ratingEnabled ? 'rating-enabled' : ''}`
              : `rcw-message ${ratingEnabled ? 'rating-enabled' : ''}`
          }
          key={`${index}-${format(new Date(message.timestamp), 'hh:mm')}`}
        >
          {profileAvatar && message.showAvatar && <img src={profileAvatar} className="rcw-avatar" alt="profile" />}

          {/* <div className={""}> */}

          {getComponentToRender(message)}
          {message.quickButtons && (
            <>
              <div style={{ width: '100%' }} />
              <div>
                {/* <div style={{width: "100%"}}/> */}
                <div className="quick-buttons-container">
                  <ul className="quick-buttons">
                    {message.quickButtons?.map((button, index) => (
                      <li className="quick-list-button" key={`${button.label}-${index}`}>
                        {getQuickButtonsToRender(button, message.customId)}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </>
          )}

          {/* </div> */}
        </div>
      ))}

      <QuickButtons onInteractiveButtonClicked={onInteractiveButtonClicked} />
      {activeTypingIndicators.map((obj) => (
        <Loader key={obj.key} typing img={profileAvatar} stage={obj.stage} partialContent={obj.partialContent} />
      ))}
      <div ref={messageRef} />
    </div>
  );
}

export default Messages;
